import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Tablet, Trash2, Clock } from "lucide-react";
import AdminTabs from "@/components/AdminTabs";
import { api } from "@/lib/api";
import {
  initiatePairing,
  listPairedDevices,
  unpairDevice,
} from "@/lib/childDevice";
import { useSEO } from "@/lib/seo";

/**
 * Parent's admin device: mint pairing codes, see paired tablets, unpair.
 *
 * Flow:
 *  1. Parent picks "Add a tablet" → modal shows a 6-char code + 10-min timer
 *  2. Parent walks the code to the child's tablet → types it into /pair-device
 *  3. Once claimed, the tablet appears in the "Paired devices" list below
 *  4. Tap Unpair to revoke — the tablet drops out of child mode within 10s
 */
export default function AdminChildDevices() {
  useSEO({
    title: "Child devices",
    description: "Manage tablets paired as communication-only child devices.",
    path: "/admin/child-devices",
  });
  const [devices, setDevices] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState("");
  const [loading, setLoading] = useState(true);
  const [showMint, setShowMint] = useState(false);
  const [minting, setMinting] = useState(false);
  const [activeCode, setActiveCode] = useState(null);
  const [label, setLabel] = useState("Child tablet");
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [list, profilesResp] = await Promise.all([
        listPairedDevices(),
        api.get("/profiles"),
      ]);
      setDevices(list);
      const profs = profilesResp.data?.profiles || profilesResp.data || [];
      setProfiles(profs);
      // Default to the first profile if the parent hasn't picked one yet
      setSelectedProfileId((cur) => cur || (profs[0]?.id || ""));
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load devices.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Refresh every 8s while the page is open so a fresh claim shows up
    // without the parent having to refresh manually.
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, []);

  const mint = async () => {
    setError("");
    if (!selectedProfileId) {
      setError("Pick which profile this tablet should show.");
      return;
    }
    setMinting(true);
    try {
      const data = await initiatePairing({
        label: label.trim() || "Child tablet",
        default_profile_id: selectedProfileId,
      });
      setActiveCode({
        code: data.code,
        expires_at: data.expires_at,
        pairing_id: data.pairing_id,
      });
      setShowMint(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not generate a code.");
    } finally {
      setMinting(false);
    }
  };

  const unpair = async (d) => {
    if (!window.confirm(`Unpair "${d.label}"? The tablet will leave child-only mode within 10 seconds.`)) {
      return;
    }
    try {
      await unpairDevice(d.id);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not unpair this device.");
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--cream)] px-4 sm:px-6 py-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/profiles" className="pill-btn" data-testid="child-devices-back">
            <ArrowLeft size={16} />
          </Link>
          <h1 className="display text-2xl sm:text-3xl font-extrabold flex items-center gap-2">
            <Tablet size={24} aria-hidden /> Child devices
          </h1>
          <div className="flex-1" />
          <button
            type="button"
            onClick={mint}
            disabled={minting}
            className="pill-btn primary"
            data-testid="child-devices-add"
          >
            <Plus size={16} />
            <span className="hidden sm:inline">Add a tablet</span>
          </button>
        </div>

        <AdminTabs current="child-devices" />

        <p className="text-sm text-[color:var(--ink)]/70 mb-4">
          Pair a tablet to lock it into communication-only mode. The child
          tablet won't show Settings, billing, or edit controls until you
          unpair it from this page.
        </p>

        {error && (
          <div
            className="brutal-card bg-red-50 p-3 text-sm text-red-700 mb-4"
            data-testid="child-devices-error"
          >
            {error}
          </div>
        )}

        {showMint && activeCode && (
          <div
            className="brutal-card bg-[color:var(--gold)] p-5 mb-6 text-center"
            data-testid="child-devices-active-code"
          >
            <div className="text-xs uppercase tracking-[0.18em] font-extrabold mb-2">
              Pairing code
            </div>
            <div
              className="text-5xl sm:text-6xl font-extrabold tracking-[0.18em] font-mono"
              data-testid="child-devices-code-value"
            >
              {activeCode.code}
            </div>
            <div className="text-xs text-[color:var(--ink)]/70 mt-3 flex items-center justify-center gap-1">
              <Clock size={12} /> Expires in 10 minutes. Single-use.
            </div>
            <p className="text-sm mt-4 max-w-md mx-auto">
              On the child's tablet, open{" "}
              <code className="px-1.5 py-0.5 bg-[color:var(--cream)] rounded border border-[color:var(--ink)]">
                /pair-device
              </code>{" "}
              and type this code.
            </p>
            <button
              type="button"
              onClick={() => setShowMint(false)}
              className="pill-btn mt-3"
              data-testid="child-devices-dismiss-code"
            >
              Done
            </button>
          </div>
        )}

        <div className="brutal-card bg-white p-5">
          <h2 className="text-lg font-extrabold mb-3">Paired tablets</h2>
          {loading && (
            <div className="text-sm text-[color:var(--ink)]/60">Loading…</div>
          )}
          {!loading && devices.length === 0 && (
            <div
              className="text-sm text-[color:var(--ink)]/60 py-6 text-center"
              data-testid="child-devices-empty"
            >
              No tablets paired yet. Tap <strong>Add a tablet</strong> above to
              get started.
            </div>
          )}
          {!loading && devices.length > 0 && (
            <ul className="divide-y" data-testid="child-devices-list">
              {devices.map((d) => (
                <li
                  key={d.id}
                  className="py-3 flex items-center gap-3"
                  data-testid={`child-device-${d.id}`}
                >
                  <Tablet size={20} className="shrink-0 text-[color:var(--ink)]/60" />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{d.label}</div>
                    <div className="text-xs text-[color:var(--ink)]/60 truncate">
                      Paired {new Date(d.claimed_at || d.created_at).toLocaleString()}
                      {d.last_seen_at && (
                        <> · last seen {new Date(d.last_seen_at).toLocaleString()}</>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => unpair(d)}
                    className="pill-btn"
                    aria-label={`Unpair ${d.label}`}
                    data-testid={`child-device-unpair-${d.id}`}
                  >
                    <Trash2 size={14} />
                    <span className="hidden sm:inline">Unpair</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-6 brutal-card bg-white p-5">
          <h3 className="font-extrabold mb-3">Next code settings</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-sm">
              <span className="block mb-1 font-bold">Tablet label</span>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                maxLength={80}
                className="brutal-input w-full"
                data-testid="child-devices-label-input"
              />
            </label>
            <label className="text-sm">
              <span className="block mb-1 font-bold">Show this profile</span>
              <select
                value={selectedProfileId}
                onChange={(e) => setSelectedProfileId(e.target.value)}
                className="brutal-input w-full"
                data-testid="child-devices-profile-select"
              >
                {profiles.length === 0 && (
                  <option value="">No profiles yet — create one first</option>
                )}
                {profiles.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p className="text-xs text-[color:var(--ink)]/60 mt-3">
            The tablet will open straight to this profile's board and stay
            locked there. You can pair more tablets later (one per child).
          </p>
        </div>
      </div>
    </div>
  );
}
